import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  GetMyCareerOverviewQuery,
  GetMyCareerOverviewQueryVariables,
  GetPersonCareerQuery,
  GetPersonCareerQueryVariables,
} from "types/graphql-schema";

import getMyCareerOverviewQuery from "@apps/org-settings/graphql/get-my-career-overview-query";
import getPersonCareerQuery from "@apps/org-settings/graphql/get-person-career-query";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import { ButtonSize } from "@components/button/button-constants";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import Loading from "@components/loading/loading";
import { assertEdgesNonNull } from "@helpers/helpers";

import SettingsPageHeader from "../settings-page-header";
import { CareerTrackFormContent } from "./career-track-form/career-track-form";
import CareerTrackViewSwitcher from "./career-track-view-switcher";
import { getRolesAndCompetenciesBreadcrumbs } from "./helpers";
import PersonRoleEmptyState from "./person-role-empty-state";
import { BuildWithAiButton } from "./role-framework-cta";
import useRolesAskAiSidebar from "./use-roles-ask-ai-sidebar";

// One person on the ladder: their track's table narrowed to their level, with
// the criteria written for them alone. Someone without a role has no table
// yet, so the page says so.
//
// Everyone else sees their own, read-only, and a manager their direct
// reports' too: they edit the person's own criteria, and see the rest
// read-only.
const PersonCareerView = ({
  organizationId,
  canManageTracks,
}: {
  organizationId: number;
  canManageTracks: boolean;
}) => {
  const label = useLabel();
  const askAiSidebar = useRolesAskAiSidebar();
  const currentUser = currentUserVar();
  const { userId: userIdParam } = useParams<{ userId: string }>();
  const userId = Number(userIdParam);
  const { data, loading, refetch } = useQuery<
    GetPersonCareerQuery,
    GetPersonCareerQueryVariables
  >(getPersonCareerQuery, {
    fetchPolicy: "network-only",
    variables: { organizationId, userId },
  });

  const user = data?.user;
  const isSelf = userId === currentUser.id;
  // Admins and the person's manager (the server says which).
  const canEditCriteria = !!data?.canEditUserCareerCriteria;
  const canView = canManageTracks || isSelf || canEditCriteria;
  // Everyone else picks between themselves and their reports, not every
  // track and person: loaded now, so a report added since sign-in is there.
  const { data: myCareerData } = useQuery<
    GetMyCareerOverviewQuery,
    GetMyCareerOverviewQueryVariables
  >(getMyCareerOverviewQuery, { skip: canManageTracks });
  const me = myCareerData?.me;
  const onlyPeople = useMemo(
    () =>
      canManageTracks
        ? undefined
        : me
          ? [me, ...assertEdgesNonNull(me.directReports)]
          : [],
    [canManageTracks, me],
  );
  const breadcrumbs = getRolesAndCompetenciesBreadcrumbs(
    organizationId,
    label,
    user?.name,
  );
  // Someone can hold more than one role: show the one picked, or the first.
  const [chosenRoleId, setChosenRoleId] = useState<number | null>(null);
  const roles = user?.currentRoles ?? [];
  const role = roles.find(({ id }) => id === chosenRoleId) ?? roles[0];
  const roleOptionFor = (item: (typeof roles)[number]) => ({
    label: `${item.title} (${item.careerTrack.title})`,
    value: item.id,
  });

  if (user && role && canView) {
    return (
      <CareerTrackFormContent
        roleChooser={(saveFirst) =>
          roles.length > 1 && (
            <div
              className="flex items-center gap-3 mb-3 shrink-0 text-sm"
              aria-label="Person role chooser"
            >
              <span className="text-gray-600">
                {`${user.name} has multiple roles`}
              </span>
              <div className="w-72">
                <ComboboxGeneric
                  aria-label="Person role select"
                  prefix="Showing"
                  buttonProps={{ size: ButtonSize.medium }}
                  options={roles.map(roleOptionFor)}
                  value={roleOptionFor(role)}
                  onChangeValue={async (option) => {
                    // The other role opens a new form from this page's copy
                    // of their criteria. So finish saving here, and reload
                    // that copy, before switching. A failed save stays put.
                    if (!(await saveFirst())) {
                      return;
                    }
                    await refetch();
                    setChosenRoleId(option.value);
                  }}
                />
              </div>
            </div>
          )
        }
        // A different person, or the same one in another role, starts over.
        key={`${user.id}-${role.id}`}
        organizationId={organizationId}
        careerTrackIdProp={role.careerTrack.id}
        canManageTrack={canManageTracks}
        canEditPersonCriteria={canEditCriteria}
        onlyPeople={onlyPeople}
        person={{
          id: user.id,
          name: user.name,
          avatar: user.avatar,
          level: role.level,
          criteria: data.userCareerCriteria,
          criteriaVersion: data.userCareerCriteriaVersion,
        }}
        onTrackCreated={() => undefined}
      />
    );
  }

  return (
    <div>
      <SettingsPageHeader breadcrumbs={breadcrumbs}>
        {askAiSidebar.isAvailable && <BuildWithAiButton />}
      </SettingsPageHeader>
      <div className={askAiSidebar.contentClassName}>
        <div className="flex items-center gap-3 mb-3">
          <CareerTrackViewSwitcher
            organizationId={organizationId}
            onlyPeople={onlyPeople}
            person={
              user
                ? { id: user.id, name: user.name, avatar: user.avatar }
                : undefined
            }
          />
        </div>
        {loading ? (
          <div className="flex justify-center mt-8">
            <Loading />
          </div>
        ) : user && !canView ? (
          <div className="mt-8 text-center text-sm text-gray-500">
            You can only see your own career track and your direct
            reports&apos;.
          </div>
        ) : user ? (
          // Picking a role refetches this page, which then shows their track.
          <PersonRoleEmptyState
            organizationId={organizationId}
            person={user}
            canManageTracks={canManageTracks}
            isSelf={isSelf}
          />
        ) : (
          <div className="mt-8 text-center text-sm text-gray-500">
            Person not found
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonCareerView;
